import React, { ReactElement } from "react";
import { Typography, Box } from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import TuneIcon from "@mui/icons-material/Tune";
import { grey } from "@mui/material/colors";
import { styled } from "@mui/material/styles";
import {
  DataGridPremium,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridToolbarContainer,
  GridDensity,
  GridFilterModel,
  gridClasses,
  useGridApiRef,
  GridToolbar,
  GridAggregationModel,
  GridRowGroupingModel,
} from "@mui/x-data-grid-premium";
import { useAppDispatch } from "src/app-redux/hooks";
import { deepCompireObjects } from "src/common/utils/helpers";
import { Loader } from "src/components/atoms/Loader";
import CommonUniqueValuesFilter from "src/components/molecules/CommonUniqueValuesFilter";
import { useStyles } from "./styles";

const StyledGridOverlay = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
  "& .ant-empty-img-1": {
    fill: theme.palette.mode === "light" ? "#aeb8c2" : "#262626",
  },
  "& .ant-empty-img-2": {
    fill: theme.palette.mode === "light" ? "#f5f5f7" : "#595959",
  },
  "& .ant-empty-img-3": {
    fill: theme.palette.mode === "light" ? "#dce0e6" : "#434343",
  },
  "& .ant-empty-img-4": {
    fill: theme.palette.mode === "light" ? "#fff" : "#1c1c1c",
  },
  "& .ant-empty-img-5": {
    fillOpacity: theme.palette.mode === "light" ? "0.8" : "0.08",
    fill: theme.palette.mode === "light" ? "#f5f5f5" : "#fff",
  },
}));
function CustomNoRowsOverlay() {
  return (
    <StyledGridOverlay>
      <svg width="70" height="50" viewBox="0 0 184 152" aria-hidden focusable="false">
        <g fill="none" fillRule="evenodd">
          <g transform="translate(24 31.67)">
            <ellipse className="ant-empty-img-5" cx="67.797" cy="106.89" rx="67.797" ry="12.668" />
            <path
              className="ant-empty-img-1"
              d="M122.034 69.674L98.109 40.229c-1.148-1.386-2.826-2.225-4.593-2.225h-51.44c-1.766 0-3.444.839-4.592 2.225L13.56 69.674v15.383h108.475V69.674z"
            />
            <path
              className="ant-empty-img-2"
              d="M33.83 0h67.933a4 4 0 0 1 4 4v93.344a4 4 0 0 1-4 4H33.83a4 4 0 0 1-4-4V4a4 4 0 0 1 4-4z"
            />
            <path
              className="ant-empty-img-3"
              d="M42.678 9.953h50.237a2 2 0 0 1 2 2V36.91a2 2 0 0 1-2 2H42.678a2 2 0 0 1-2-2V11.953a2 2 0 0 1 2-2zM42.94 49.767h49.713a2.262 2.262 0 1 1 0 4.524H42.94a2.262 2.262 0 0 1 0-4.524zM42.94 61.53h49.713a2.262 2.262 0 1 1 0 4.525H42.94a2.262 2.262 0 0 1 0-4.525zM121.813 105.032c-.775 3.071-3.497 5.36-6.735 5.36H20.515c-3.238 0-5.96-2.29-6.734-5.36a7.309 7.309 0 0 1-.222-1.79V69.675h26.318c2.907 0 5.25 2.448 5.25 5.42v.04c0 2.971 2.37 5.37 5.277 5.37h34.785c2.907 0 5.277-2.421 5.277-5.393V75.1c0-2.972 2.343-5.426 5.25-5.426h26.318v33.569c0 .617-.077 1.216-.221 1.789z"
            />
          </g>
          <path
            className="ant-empty-img-3"
            d="M149.121 33.292l-6.83 2.65a1 1 0 0 1-1.317-1.23l1.937-6.207c-2.589-2.944-4.109-6.534-4.109-10.408C138.802 8.102 148.92 0 161.402 0 173.881 0 184 8.102 184 18.097c0 9.995-10.118 18.097-22.599 18.097-4.528 0-8.744-1.066-12.28-2.902z"
          />
          <g className="ant-empty-img-4" transform="translate(149.65 15.383)">
            <ellipse cx="20.654" cy="3.167" rx="2.849" ry="2.815" />
            <path d="M5.698 5.63H0L2.898.704zM9.259.704h4.985V5.63H9.259z" />
          </g>
        </g>
      </svg>
      <Box>No Rows</Box>
    </StyledGridOverlay>
  );
}
const customSpan: any = {
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  justifyContent: "center",
  alignContent: "center",
  alignItems: "center",
  position: "absolute",
  fontWeight: 500,
  fontSize: "0.75rem",
  minWidth: 20,
  lineHeight: 1,
  padding: "0 6px",
  height: 20,
  borderRadius: 10,
  zIndex: 1,
  backgroundColor: "#1976d2",
  color: "#fff",
  top: 0,
  transform: "scale(1) translate(50%, -50%)",
};
interface Props {
  columns: any;
  loader?: boolean;
  rows?: any;
  height?: number;
  defaultPerPageRecord?: number;
  checkboxSelection?: boolean;
  onSelectedAll?: (flag: boolean) => void;
  onRowSelection?: any;
  onSelectionModelChange?: any;
  editRow?: any;
  editRowChange?: any;
  modifyColumns?: boolean;
  updateSelection?: any;
  showOnlyDate?: boolean;
  disableSelectAllCheckBox?: boolean;
  onRowDoubleClick?: any;
  onRowSingleClick?: any;
  paginationMode?: any;
  filterMode?: any;
  sortingMode?: any;
  minHeight?: number;
  reCallSelectAllFun?: boolean;
  onSelectedAllReCall?: any;
  preSelectionModel?: any;
  pinnedLastCol?: boolean;
  customGridSettingSave?: boolean;
  gridName?: string;
  sortingby?: string;
  sortById?: boolean;
  cDensity?: string;
  singleRecordSelection?: boolean;
  initVal?: any;
  singleClickEnable?: boolean;
  showPagination?: boolean;
  sortByColName?: string;
  notshowHeaderCheckBox?: boolean;
  processRowUpdate?: any;
  newEditingApi?: boolean;
  setTemprorySetting?: ({ col, pageSize, dan, aggM, rowM }: any) => void;
  aggModal?: GridAggregationModel;
  rowGModal?: GridRowGroupingModel;
  initialColumnVisibilityModel?: Record<string, boolean>;
}

export default function CommonXGrid({
  columns,
  loader = false,
  rows = [],
  height,
  defaultPerPageRecord,
  checkboxSelection = false,
  onRowSelection,
  onSelectedAll,
  onSelectedAllReCall,
  onSelectionModelChange,
  editRow = null,
  editRowChange = null,
  modifyColumns = false,
  disableSelectAllCheckBox,
  onRowDoubleClick,
  showOnlyDate,
  paginationMode = "client",
  filterMode = "client",
  sortingMode = "client",
  minHeight,
  reCallSelectAllFun = false,
  preSelectionModel,
  pinnedLastCol,
  customGridSettingSave,
  gridName = "",
  sortingby = "desc",
  sortById = false,
  cDensity = "compact",
  singleRecordSelection = false,
  initVal,
  onRowSingleClick,
  singleClickEnable = false,
  showPagination = true,
  sortByColName,
  notshowHeaderCheckBox = false,
  processRowUpdate,
  newEditingApi = false,
  aggModal,
  rowGModal,
  setTemprorySetting,
  initialColumnVisibilityModel,
}: Props): ReactElement {
  const checkForAggrationColsDisable = (act: boolean) => {
    const col: any = act ? displayColumns : columns;
    col.map((item: any) => {
      if (item.type === "number") {
        item.aggregable = true;
      } else if (item.type === "action" || item.field === "actions") {
        item.groupable = false;
        item.aggregable = false;
      } else {
        item.aggregable = false;
      }
    });
    return col;
  };
  const classes = useStyles();
  const apiRef = useGridApiRef();
  const [pageSize, setPageSize] = React.useState(defaultPerPageRecord || 1000);
  const [page, setPage] = React.useState(initVal?.page || 0);
  const [displayColumns, setDisplayColumns] = React.useState<any>(
    checkForAggrationColsDisable(false)
  );
  const [displayRows, setDisplayRows] = React.useState<any>(rows);
  const [dan, setDan] = React.useState<string>(cDensity);
  const [filtersRows, setFiltersRows] = React.useState<any>(rows);
  const [selectionModel, setSelectionModel] = React.useState<any>([]);
  const [prevSelectionModel, setPrevSelectionModel] = React.useState<any>([]);
  const [internalLoader, setInternalLoader] = React.useState<boolean>(false);
  const [showUniqueValueFilter, setShowUniqueValueFilter] = React.useState<boolean>(false);
  const [fristTimeSortInit, setFristTimeSortInit] = React.useState<boolean>(true);
  const [selectedFilter, setSelectedFilter] = React.useState<any[]>([]);
  const [filterModel, setFilterModel] = React.useState<GridFilterModel>({
    items: initVal?.filterModel ? [...initVal.filterModel?.items] : [],
  });
  const [aggregationModel, setAggregationModel] = React.useState<GridAggregationModel>(
    aggModal || {}
  );
  const [rowGroupingModel, setRowGroupingModel] = React.useState<GridRowGroupingModel>(
    rowGModal || []
  );
  const checkForInputFileds = () => {
    if (sortById) {
      return columns[0]?.field;
    } else {
      let res = false;
      let sortCol = "inputDate";
      if (sortByColName) {
        res = true;
        sortCol = sortByColName;
      } else {
        for (let i = 0; i < columns.length; i++) {
          if (
            columns[i]?.field === "inputDate" ||
            columns[i]?.field === "tranDate" ||
            columns[i]?.field === "trandate"
          ) {
            res = true;
            if (columns[i]?.field === "tranDate") {
              sortCol = "tranDate";
              break;
            }
            if (columns[i]?.field === "trandate") {
              sortCol = "trandate";
              break;
            } else {
              sortCol = "inputDate";
              break;
            }
          }
        }
      }

      return res ? sortCol : columns[0]?.field ? columns[0]?.field : "id";
    }
  };
  const [sortModel, setSortModel] = React.useState<any>(
    initVal?.sortModel
      ? [...initVal.sortModel]
      : [{ field: checkForInputFileds(), sort: sortingby ? sortingby : "desc" }]
  );
  const [initialState] = React.useState<any>({
    pinnedColumns: {
      left: [GRID_CHECKBOX_SELECTION_COL_DEF.field],
      right: pinnedLastCol && ["actions"],
    },
    ...(initialColumnVisibilityModel && Object.keys(initialColumnVisibilityModel).length > 0
      ? { columns: { columnVisibilityModel: initialColumnVisibilityModel } }
      : {}),
  });
  const handlePageChange = (params: any) => {
    setPageSize(params);
  };
  function CustomToolbar() {
    return (
      <GridToolbarContainer>
        <GridToolbar />
        <Box
          display={"flex"}
          style={{ cursor: "pointer", marginTop: 5 }}
          onClick={() => setShowUniqueValueFilter(true)}
        >
          <TuneIcon style={{ fontSize: 19, marginLeft: 5, position: "relative" }} />
          {Object.keys(selectedFilter).length > 0 && (
            <Typography style={customSpan}>{Object.keys(selectedFilter).length}</Typography>
          )}
          <Box ml={1} fontSize={12}>
            FIND UNIQUE VALUES
          </Box>
        </Box>
      </GridToolbarContainer>
    );
  }
  const setGridColumns = async (col: any[]) => {
    await setDisplayColumns(col);
    // if (!deepCompireObjects(columns, displayColumns)) {
    //   setTemprorySetting &&
    //     setTemprorySetting({ col, pageSize, dan, aggregationModel, rowGroupingModel });
    // }
  };
  const checkPrvAppliedFilters = (csf: string, suf: any) => {
    const res: any[] = [];
    if (suf.length) {
      suf.map((it: any) => {
        if (it.checked) res.push(it[csf]);
      });
    }
    return res;
  };
  const onApplySelRecordFilter = (data: any, clearAll: boolean, restoreOrg: boolean) => {
    setInternalLoader(true);
    if (clearAll || data.length === 0) {
      setSelectedFilter([]);
      setDisplayRows(rows);
      setFiltersRows(rows);
    } else {
      const arrayFilters: any[] = [];
      let selRecord: any[] = rows;
      for (let i = 0; i < data.length; i++) {
        const filterFor = Object.keys(data[i])[0];
        const filtersOption = checkPrvAppliedFilters(filterFor, data[i][filterFor]);
        if (filtersOption.length) {
          selRecord = selRecord.filter((item: any) => filtersOption.includes(item[filterFor]));
          arrayFilters.push(data[i]);
        }
        setSelectedFilter(arrayFilters);
      }
      if (restoreOrg) {
        setFiltersRows(rows);
      } else {
        setFiltersRows(selRecord);
      }
      setDisplayRows(selRecord);
    }
    setInternalLoader(false);
  };
  React.useEffect(() => {
    if (rows?.length || displayRows?.length) {
      if (Object.keys(aggModal || {}).length > 0 && Object.keys(aggregationModel).length == 0) {
        setAggregationModel(aggModal || {});
      }
      if (rowGModal?.length && !rowGroupingModel.length) setRowGroupingModel(rowGModal);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aggModal, rowGModal, rows, displayRows]);
  React.useEffect(() => {
    if (defaultPerPageRecord && defaultPerPageRecord !== pageSize)
      setPageSize(defaultPerPageRecord);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultPerPageRecord]);
  React.useEffect(() => {
    if (cDensity && dan !== cDensity) setDan(cDensity);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cDensity]);
  React.useEffect(() => {
    if (!deepCompireObjects(columns, displayColumns)) {
      setDisplayColumns(checkForAggrationColsDisable(false));
    } else {
      if (displayColumns?.length || columns?.length)
        setDisplayColumns(checkForAggrationColsDisable(Boolean(displayColumns?.length)));
    }
  }, [columns]);

  React.useEffect(() => {
    if (!deepCompireObjects(rows, displayRows)) {
      setDisplayRows(rows || []);
      setFiltersRows(rows || []);
      if (Object.keys(selectedFilter).length > 0) {
        onApplySelRecordFilter(selectedFilter, false, false);
      }
    }
  }, [rows]);
  const getGroupSelectionRec = (IDs: any) => {
    const result: any[] = [];
    IDs?.map((id: any) => {
      result.push(id);
      if (typeof id === "string" && id.includes("auto-generated-row-")) {
        const [first, ...rest] = id?.split("/");
        displayRows.map((row: any) => {
          if (row[first.split("-")[3]] === rest.join("/")) {
            result.push(row.id);
          }
        });
      } else {
        result.push(id);
      }
    });
    return [...new Set(result)];
  };
  React.useEffect(() => {
    if (preSelectionModel?.length) {
      setSelectionModel(preSelectionModel);
    }
  }, [preSelectionModel]);
  const handleRowSelection = (IDs: any) => {
    let idsArray = IDs;
    if (singleRecordSelection) {
      setPrevSelectionModel(selectionModel);
      setSelectionModel([IDs.at(-1)]);
    } else {
      IDs?.map((id: string) => {
        if (typeof id === "string" && id.includes("auto-generated-row-")) {
          idsArray = getGroupSelectionRec(IDs);
        }
      });
      setPrevSelectionModel(selectionModel);
      setSelectionModel(idsArray);
    }

    if (onSelectionModelChange) {
      onSelectionModelChange({ selectionModel: idsArray });
    }
  };

  React.useEffect(() => {
    const difference = singleRecordSelection
      ? selectionModel
      : selectionModel
          ?.filter((rowID: any) => !prevSelectionModel.includes(rowID))
          .concat(prevSelectionModel?.filter((rowID: any) => !selectionModel.includes(rowID)));
    const selectedRowData: any = rows
      .filter((row: any) => {
        return difference?.find((id: any) => row.id === id);
      })
      .map((row: any) => ({
        data: row,
        isSelected: singleRecordSelection
          ? true
          : prevSelectionModel?.length < selectionModel.length,
      }));
    const [rowData] = selectedRowData;
    onRowSelection &&
      rowData &&
      onRowSelection(
        singleRecordSelection ? selectedRowData[selectedRowData.length - 1] : selectedRowData[0]
      );
    if (
      !disableSelectAllCheckBox &&
      reCallSelectAllFun &&
      onSelectedAll &&
      onSelectedAllReCall &&
      reCallSelectAllFun &&
      selectedRowData
    ) {
      onSelectedAllReCall(selectedRowData);
    }
  }, [selectionModel]);
  const griCOlumnSetting = () => {
    const { columns } = apiRef.current?.state as any;
    const cols: any[] = [];
    columns.all.map((item: string) => {
      cols.push(columns?.lookup[item]);
    });
    if (cols) {
      setGridColumns(cols);
    }
  };
  return (
    <>
      <Loader open={internalLoader} />
      <div className={classes.root} style={{ minHeight: minHeight || "inherit" }}>
        <div style={{ height: height || "inherit", width: "100%" }}>
          <DataGridPremium
            apiRef={apiRef}
            sx={{
              "& .MuiDataGrid-cell--withRenderer": {
                fontSize: 12,
                fontFamily: ["Inter", "Arial", "sans-serif"].join(","),
              },
              "& .MuiDataGrid-cell": {
                fontFamily: ["Inter", "Arial", "sans-serif"].join(","),
                fontSize: 12,
                fontWeight: 450,
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontFamily: ["Inter", "Arial", "sans-serif"].join(","),
                fontSize: 12,
                fontWeight: "bold",
              },
              "& .MuiDataGrid-columnHeader.MuiDataGrid-columnHeader--alignCenter.MuiDataGrid-columnHeaderCheckbox":
                {
                  display: notshowHeaderCheckBox ? "none" : "inherit",
                },
              [`& .${gridClasses.row}`]: {
                bgcolor: (theme) => (theme.palette.mode === "light" ? grey[200] : grey[900]),
              },
            }}
            // @ts-ignore - onPageSizeChange exists in v5.17.19 but not in TypeScript definitions
            onPageSizeChange={handlePageChange}
            sortingOrder={["desc", "asc"]}
            headerHeight={37}
            rowHeight={35}
            initialState={initialState}
            selectionModel={selectionModel}
            className={classes.gridContainer}
            loading={loader}
            columns={displayColumns}
            rows={displayRows || []}
            pageSize={pageSize}
            pagination={showPagination || true}
            checkboxSelection={checkboxSelection}
            disableSelectionOnClick
            components={{
              Toolbar: CustomToolbar,
              NoRowsOverlay: CustomNoRowsOverlay,
            }}
            density={(cDensity as GridDensity) || "compact"}
            page={page}
            onPageChange={(newPage: number) => {
              if (!deepCompireObjects(newPage, page)) {
                setPage(newPage);
              }
            }}
            sortModel={sortModel}
            onSortModelChange={(newSortModel) => {
              if (
                !deepCompireObjects(newSortModel, sortModel) &&
                !fristTimeSortInit &&
                newSortModel.length
              ) {
                setSortModel(newSortModel);
              } else {
                setSortModel(sortModel);
                setFristTimeSortInit(false);
              }
            }}
            filterModel={{ ...filterModel }}
            onFilterModelChange={(newFilterModel) => {
              if (!deepCompireObjects(newFilterModel, filterModel)) {
                setFilterModel(newFilterModel);
              } else {
                setFilterModel(filterModel);
              }
            }}
            onColumnOrderChange={() => griCOlumnSetting()}
            onColumnResize={() => griCOlumnSetting()}
            onColumnVisibilityModelChange={() => griCOlumnSetting()}
            onColumnWidthChange={() => griCOlumnSetting()}
            componentsProps={{
              toolbar: {
                csvOptions: { disableToolbarButton: false },
                printOptions: { disableToolbarButton: false },
              },
            }}
            rowsPerPageOptions={[100, 1000, 5000, 100000]}
            onColumnHeaderClick={(e: any, v: any) => {
              if (disableSelectAllCheckBox) {
                v.target.checked = false;
              } else {
                if (e.field === "__check__") {
                  v?.stopPropagation();
                  onSelectedAll && onSelectedAll(v.target?.checked);
                }
              }
            }}
            filterMode={filterMode}
            paginationMode={paginationMode}
            sortingMode={sortingMode}
            editMode="cell"
            onCellEditCommit={(e: any, event: any) => {
              event.stopPropagation();
              editRow && editRow(e);
            }}
            onEditCellPropsChange={(e: any, event: any) => {
              event.stopPropagation();
              !newEditingApi && editRowChange && editRowChange(e);
            }}
            disableExtendRowFullWidth={true}
            onSelectionModelChange={handleRowSelection}
            onRowDoubleClick={(e: any, muiE: any) => {
              muiE.stopPropagation();
              if (gridName === "Doc") {
                !singleClickEnable && onRowDoubleClick && onRowDoubleClick(e);
              } else {
                onRowDoubleClick && onRowDoubleClick(e);
              }
            }}
            onRowClick={(e: any, muiE: any) => {
              muiE.stopPropagation();
              if (gridName === "Doc") {
                singleClickEnable && onRowSingleClick && onRowSingleClick(e);
              } else {
                onRowSingleClick && onRowSingleClick(e);
              }
            }}
            columnThreshold={0}
            processRowUpdate={processRowUpdate}
            aggregationModel={aggregationModel}
            onAggregationModelChange={(newModel) => setAggregationModel(newModel)}
            rowGroupingModel={rowGroupingModel}
            onRowGroupingModelChange={(model) => setRowGroupingModel(model)}
            experimentalFeatures={{ aggregation: true, newEditingApi: newEditingApi || false } as any}
          />
        </div>
      </div>
      {showUniqueValueFilter && (
        <CommonUniqueValuesFilter
          closed={() => {
            setShowUniqueValueFilter(false);
          }}
          selRec={selectedFilter}
          columns={columns}
          rows={filtersRows}
          onApplySelRecordFilter={onApplySelRecordFilter}
        />
      )}
    </>
  );
}
